import React, { Component, ErrorInfo, ReactNode } from 'react'
import { posthog } from '@/analytics/posthog'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to PostHog
    if (import.meta.env.PROD) {
      posthog.capture('$exception', {
        $exception_type: 'React Error Boundary',
        $exception_message: error.message,
        $exception_stack_trace_raw: error.stack,
        $exception_personURL: posthog.get_session_replay_url?.(),
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      })
    }

    console.error('Error caught by boundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center p-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">Something went wrong</h2>
            <p className="text-muted-foreground mb-6">
              We've been notified about this error and will fix it soon.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}